import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, addDoc, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { getAuth } from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import './TimeOff.css';
import ScopedPortalPopup from '../components/ScopedPortalPopup';
import {
  parseYmd,
  formatYmd,
  formatRange,
  isAbsence,
  isPartialDay,
  describeEntry,
  DAY_FULL,
  DAY_LEAVE_EARLY,
  DAY_ARRIVE_LATE,
  ENTRY_REQUEST,
} from '../utils/timeOffUtils';

/* ---------------------------------------------------------------------------
   DATE HELPERS

   Time off dates are stored as plain "YYYY-MM-DD" strings. Two consequences:

   1. They compare correctly with < and > as STRINGS. "2025-03-09" < "2025-03-10"
      both lexicographically and chronologically, so the overlap maths below
      needs no Date objects at all.
   2. new Date("2025-03-10") parses as UTC midnight, which renders as the
      PREVIOUS day for anyone west of Greenwich. parseYmd builds a local date
      instead, so a request never displays a day earlier than it was made for.
--------------------------------------------------------------------------- */
// parseYmd / formatYmd / formatRange now come from utils/timeOffUtils so the
// calendar and both Time Off pages format dates identically.

// Two closed date ranges overlap when each one starts on or before the other
// one ends. A missing end date is treated as a single-day request.
const rangesOverlap = (aStart, aEnd, bStart, bEnd) => {
  if (!aStart || !bStart) return false;
  const aS = aStart;
  const aE = aEnd || aStart;
  const bS = bStart;
  const bE = bEnd || bStart;
  return aS <= bE && bS <= aE;
};

// The portion two ranges actually share, so the warning can name the exact
// contested days rather than just saying "these requests overlap".
const overlapRange = (aStart, aEnd, bStart, bEnd) => {
  const aE = aEnd || aStart;
  const bE = bEnd || bStart;
  const start = aStart > bStart ? aStart : bStart;
  const end = aE < bE ? aE : bE;
  return { start, end };
};

const countDays = (start, end) => {
  const s = parseYmd(start);
  const e = parseYmd(end || start);
  if (!s || !e) return 0;
  return Math.round((e - s) / 86400000) + 1;
};

const normalizeName = (value) => String(value || '').trim().toLowerCase();

// "Mar 3, 2026 · Leaving early at 2:30 PM" for partial days, plain range
// otherwise.
const formatEntryDates = (entry) =>
  isPartialDay(entry)
    ? `${formatYmd(entry.startDate)} · ${describeEntry(entry)}`
    : formatRange(entry.startDate, entry.endDate);

const EMPTY_REQUEST = {
  userId: '',
  startDate: '',
  endDate: '',
  dayType: DAY_FULL,
  partialTime: '',
  paidTimeType: '',
  reason: '',
  status: 'Pending',
  notes: [],
};

/* ---------------------------------------------------------------------------
   ACTIVE / INACTIVE PEOPLE

   Different records in the users collection have carried this flag under
   different names over time, so all of the known spellings are checked. An
   unrecognised record is treated as ACTIVE on purpose: hiding a request because
   a field is spelled unexpectedly is far worse than showing one extra row.
--------------------------------------------------------------------------- */
const INACTIVE_STATUS_WORDS = [
  'terminated',
  'inactive',
  'former',
  'separated',
  'archived',
  'disabled',
  'resigned',
];

const isActiveUser = (user) => {
  if (!user) return true;
  if (user.active === false) return false;
  if (user.isActive === false) return false;
  if (user.terminated === true) return false;
  const status = String(user.status || user.employmentStatus || '').trim().toLowerCase();
  if (INACTIVE_STATUS_WORDS.includes(status)) return false;
  return true;
};

const statusChipClass = (status) => {
  const s = String(status || '').trim().toLowerCase();
  if (s === 'approved') return 'to-chip to-chip-approved';
  if (s === 'denied') return 'to-chip to-chip-denied';
  if (s === 'pending') return 'to-chip to-chip-pending';
  return 'to-chip to-chip-off';
};

const TimeOff = () => {
  const [timeOffRequest, setTimeOffRequest] = useState(EMPTY_REQUEST);
  const [usersList, setUsersList] = useState([]);
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [newStartDate, setNewStartDate] = useState('');
  const [newEndDate, setNewEndDate] = useState('');
  const [showDateChangePopup, setShowDateChangePopup] = useState(false);
  const [loading, setLoading] = useState(true);

  // Holds the pending action while the user decides what to do about an
  // overlap: { mode: 'submit' | 'dateChange', conflicts: [...], payload }
  const [conflictPrompt, setConflictPrompt] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  /* Toolbar */
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortKey, setSortKey] = useState('startDate');
  const [hideInactive, setHideInactive] = useState(true);

  useEffect(() => {
    const load = async () => {
      await Promise.all([fetchUsers(), fetchTimeOffRequests()]);
      setLoading(false);
    };
    load();
  }, []);

  const fetchUsers = async () => {
    try {
      const usersRef = collection(db, 'users');
      const querySnapshot = await getDocs(usersRef);
      const userList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUsersList(userList);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchTimeOffRequests = async () => {
    try {
      const timeOffRef = collection(db, 'timeOff');
      const querySnapshot = await getDocs(timeOffRef);
      const requests = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setTimeOffRequests(requests);
    } catch (error) {
      console.error('Error fetching time off requests:', error);
    }
  };

  /* -------------------------------------------------------------------------
     OVERLAP DETECTION

     Who a request is FOR is identified by `name`, not `userId`. handleSubmit
     writes userId = the signed-in user (whoever is filling the form in), while
     `name` comes from the person picked in the dropdown. Filtering on userId
     would therefore hide every request the current user had ever entered on
     someone else's behalf, and no warning would ever appear.

     When either name is missing we deliberately DO NOT exclude the record.
     Showing an extra warning is a minor annoyance; missing a real clash is the
     failure that gets someone's time off denied.
  ------------------------------------------------------------------------- */
  const findOverlappingRequests = ({
    startDate,
    endDate,
    personName,
    ignoreRequestId,
  }) => {
    if (!startDate) return [];

    const me = normalizeName(personName);

    return timeOffRequests
      .filter((req) => {
        if (!req || !req.startDate) return false;
        if (ignoreRequestId && req.id === ignoreRequestId) return false;

        // Logged call-outs / left early are not competing requests.
        if (isAbsence(req)) return false;

        // A denied request is not competing for the day any more.
        if (String(req.status || '').trim().toLowerCase() === 'denied') return false;

        // The same person moving their own dates around is not a conflict.
        const theirName = normalizeName(req.name);
        if (me && theirName && me === theirName) return false;

        return rangesOverlap(startDate, endDate, req.startDate, req.endDate);
      })
      .map((req) => ({
        ...req,
        overlap: overlapRange(startDate, endDate, req.startDate, req.endDate),
      }))
      .sort((a, b) => String(a.startDate).localeCompare(String(b.startDate)));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTimeOffRequest({ ...timeOffRequest, [name]: value });
  };

  const handleFileChange = (e) => {
    setSelectedFiles(Array.from(e.target.files));
  };

  // The actual write. Split out of handleSubmit so it can run either directly
  // (no conflicts) or after the user confirms (conflicts present).
  const submitRequest = async (requestData) => {
    setSubmitting(true);
    try {
      const docRef = await addDoc(collection(db, 'timeOff'), requestData);
      setTimeOffRequests([...timeOffRequests, { ...requestData, id: docRef.id }]);
      setTimeOffRequest(EMPTY_REQUEST);
      setConflictPrompt(null);
      setSelectedRequest(null);
    } catch (error) {
      console.error('Error submitting time off request:', error);
      alert('There was a problem submitting your request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const partial = timeOffRequest.dayType !== DAY_FULL;
    if (partial && !timeOffRequest.partialTime) {
      alert(
        timeOffRequest.dayType === DAY_LEAVE_EARLY
          ? 'Enter the time they need to leave.'
          : 'Enter the time they will get in.'
      );
      return;
    }

    if (!partial && timeOffRequest.endDate && timeOffRequest.endDate < timeOffRequest.startDate) {
      alert('The end date cannot be before the start date.');
      return;
    }

    const auth = getAuth();
    const currentUser = auth.currentUser;
    const selectedUser = usersList.find((user) => user.id === timeOffRequest.userId);
    const personName = selectedUser
      ? `${selectedUser.firstName} ${selectedUser.lastName}`
      : 'Unknown User';

    const requestData = {
      ...timeOffRequest,
      entryType: ENTRY_REQUEST,
      // A partial day is always a single date.
      endDate: partial ? timeOffRequest.startDate : timeOffRequest.endDate,
      partialTime: partial ? timeOffRequest.partialTime : '',
      dateRequested: new Date().toISOString(),
      userId: currentUser ? currentUser.uid : '',
      name: personName,
    };

    const conflicts = findOverlappingRequests({
      startDate: requestData.startDate,
      endDate: requestData.endDate,
      personName,
    });

    if (conflicts.length > 0) {
      setConflictPrompt({ mode: 'submit', conflicts, payload: requestData });
      return;
    }

    await submitRequest(requestData);
  };

  const openRequestDetails = (request) => {
    setSelectedRequest(request);
    setNewStartDate(request.startDate || '');
    setNewEndDate(request.endDate || '');
  };

  const closeModal = () => {
    setSelectedRequest(null);
    setNoteText('');
    setSelectedFiles([]);
    setShowDateChangePopup(false);
    setConflictPrompt(null);
  };

  const saveNote = async () => {
    if (selectedRequest && (noteText.trim() !== '' || selectedFiles.length > 0)) {
      setUploading(true);
      let uploadedFiles = [];

      if (selectedFiles.length > 0) {
        const storage = getStorage();
        const uploadPromises = selectedFiles.map(async (file) => {
          const storageRef = ref(storage, `timeOff/${selectedRequest.id}/${file.name}`);
          await uploadBytes(storageRef, file);
          return await getDownloadURL(storageRef);
        });
        uploadedFiles = await Promise.all(uploadPromises);
      }

      const newNote = {
        text: noteText,
        timestamp: new Date().toLocaleString(),
        files: uploadedFiles,
      };

      const updatedNotes = Array.isArray(selectedRequest.notes)
        ? [...selectedRequest.notes, newNote]
        : [newNote];
      const requestRef = doc(db, 'timeOff', selectedRequest.id);
      await updateDoc(requestRef, { notes: updatedNotes });

      setSelectedRequest((prev) => ({ ...prev, notes: updatedNotes }));
      setTimeOffRequests((prevRequests) =>
        prevRequests.map((req) =>
          req.id === selectedRequest.id ? { ...req, notes: updatedNotes } : req
        )
      );

      setNoteText('');
      setSelectedFiles([]);
      setUploading(false);
    }
  };

  // The actual date write, split out for the same reason as submitRequest.
  const applyDateChange = async (startDate, endDate) => {
    const requestRef = doc(db, 'timeOff', selectedRequest.id);
    await updateDoc(requestRef, { startDate, endDate });
    setSelectedRequest((prev) => ({ ...prev, startDate, endDate }));
    setTimeOffRequests((prevRequests) =>
      prevRequests.map((req) =>
        req.id === selectedRequest.id ? { ...req, startDate, endDate } : req
      )
    );
    setShowDateChangePopup(false);
    setConflictPrompt(null);
  };

  const handleDateChange = async () => {
    // A partial day (leave early / come in late) is a single date.
    const partial = isPartialDay(selectedRequest || {});
    const endForSave = partial ? newStartDate : newEndDate;
    if (!selectedRequest || !newStartDate || !endForSave) return;

    if (endForSave < newStartDate) {
      alert('The end date cannot be before the start date.');
      return;
    }

    // Moving dates can land on a different clash, so the same check runs here.
    // The request being edited is excluded so it never conflicts with itself.
    const conflicts = findOverlappingRequests({
      startDate: newStartDate,
      endDate: endForSave,
      personName: selectedRequest.name,
      ignoreRequestId: selectedRequest.id,
    });

    if (conflicts.length > 0) {
      setConflictPrompt({
        mode: 'dateChange',
        conflicts,
        payload: { startDate: newStartDate, endDate: endForSave },
      });
      return;
    }

    await applyDateChange(newStartDate, endForSave);
  };

  const confirmDespiteConflicts = async () => {
    if (!conflictPrompt) return;
    if (conflictPrompt.mode === 'submit') {
      await submitRequest(conflictPrompt.payload);
    } else {
      await applyDateChange(
        conflictPrompt.payload.startDate,
        conflictPrompt.payload.endDate
      );
    }
  };

  // "Pick Different Dates" just dismisses the warning and drops the user back
  // on the form they came from, with everything they typed still in place.
  const dismissConflictAndEdit = () => {
    setConflictPrompt(null);
  };

  const conflictPeopleLabel = (conflicts) => {
    const names = Array.from(
      new Set(conflicts.map((c) => String(c.name || 'Someone').trim()))
    );
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} and ${names[1]}`;
    return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
  };

  /* --------------------------------------------------- derived list data -- */

  // Requests store the person's NAME, not a stable user id, so the active
  // lookup is keyed on the normalized name.
  const activeNameSet = useMemo(() => {
    const set = new Set();
    usersList.forEach((user) => {
      if (isActiveUser(user)) {
        set.add(normalizeName(`${user.firstName || ''} ${user.lastName || ''}`));
      }
    });
    return set;
  }, [usersList]);

  const knownNameSet = useMemo(() => {
    const set = new Set();
    usersList.forEach((user) => {
      set.add(normalizeName(`${user.firstName || ''} ${user.lastName || ''}`));
    });
    return set;
  }, [usersList]);

  const isRequestForActivePerson = (request) => {
    const name = normalizeName(request.name);
    // A name we have never seen is treated as active - see isActiveUser.
    if (!name || !knownNameSet.has(name)) return true;
    return activeNameSet.has(name);
  };

  const activePeople = useMemo(
    () =>
      usersList
        .filter(isActiveUser)
        .map((u) => ({ id: u.id, name: `${u.firstName || ''} ${u.lastName || ''}`.trim() }))
        .filter((u) => u.name)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [usersList]
  );

  const visibleRequests = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    let rows = timeOffRequests.filter((request) => {
      // Call-outs / left early are logged on Time Off Approvals, not here.
      if (isAbsence(request)) return false;
      if (hideInactive && !isRequestForActivePerson(request)) return false;
      if (statusFilter && String(request.status || '') !== statusFilter) return false;
      if (!q) return true;
      return [request.name, request.paidTimeType, request.reason, request.status]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });

    rows = [...rows].sort((a, b) => {
      if (sortKey === 'startDate') {
        // Soonest first. Requests with no start date sort last rather than
        // jumping to the top, which is what an empty string does.
        return (a.startDate || '9999-12-31').localeCompare(b.startDate || '9999-12-31');
      }
      if (sortKey === 'dateRequested') {
        // Newest submission first.
        return String(b.dateRequested || '').localeCompare(String(a.dateRequested || ''));
      }
      return String(a[sortKey] || '').localeCompare(String(b[sortKey] || ''));
    });

    return rows;
  }, [
    timeOffRequests,
    searchQuery,
    statusFilter,
    sortKey,
    hideInactive,
    activeNameSet,
    knownNameSet,
  ]);

  const requestCount = useMemo(
    () => timeOffRequests.filter((r) => !isAbsence(r)).length,
    [timeOffRequests]
  );

  /* -------------------------------------------------------------- render -- */

  return (
    <div className="time-off-page">
      <header className="to-header">
        <div>
          <h1 className="to-title">Time Off</h1>
          <p className="to-subtitle">
            Requests for vacation, personal days, leaving early or coming in
            late. You will be warned before submitting if someone else has
            already asked for the same days.
          </p>
        </div>
        <button className="blue-button" onClick={() => setSelectedRequest({})}>
          Request time off
        </button>
      </header>

      <div className="to-toolbar">
        <div className="to-field to-field-grow">
          <span className="to-field-label" id="to-search-label">
            Search
          </span>
          <input
            aria-labelledby="to-search-label"
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Name, type or reason"
          />
        </div>

        <div className="to-field">
          <span className="to-field-label" id="to-status-label">
            Status
          </span>
          <select
            aria-labelledby="to-status-label"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All statuses</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Denied">Denied</option>
          </select>
        </div>

        <div className="to-field">
          <span className="to-field-label" id="to-sort-label">
            Sort by
          </span>
          <select
            aria-labelledby="to-sort-label"
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
          >
            <option value="startDate">Start date</option>
            <option value="name">Person</option>
            <option value="status">Status</option>
            <option value="dateRequested">Recently submitted</option>
          </select>
        </div>

        <label className="to-toggle">
          <input
            type="checkbox"
            checked={hideInactive}
            onChange={(e) => setHideInactive(e.target.checked)}
          />
          Hide people no longer active
        </label>

        <div className="to-count">
          {visibleRequests.length} of {requestCount}
        </div>
      </div>

      <div className="to-table-wrap">
        <table className="to-table">
          <thead>
            <tr>
              <th>Person</th>
              <th>Dates</th>
              <th>Days</th>
              <th>Type</th>
              <th>Submitted</th>
              <th>Status</th>
              <th className="to-col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="to-empty" colSpan={7}>
                  Loading requests...
                </td>
              </tr>
            ) : visibleRequests.length === 0 ? (
              <tr>
                <td className="to-empty" colSpan={7}>
                  {requestCount === 0
                    ? 'No time off requested yet. Use Request time off to submit the first one.'
                    : 'No requests match the current search and filters.'}
                </td>
              </tr>
            ) : (
              visibleRequests.map((request) => {
                const days = isPartialDay(request)
                  ? 'Partial'
                  : countDays(request.startDate, request.endDate);
                const inactive = !isRequestForActivePerson(request);
                return (
                  <tr key={request.id} className={inactive ? 'to-row-inactive' : undefined}>
                    <td>
                      <span className="to-strong">{request.name || 'Unknown'}</span>
                      {inactive && <span className="to-sub">No longer active</span>}
                    </td>
                    <td>{formatEntryDates(request)}</td>
                    <td>{days || <span className="to-muted">—</span>}</td>
                    <td>{request.paidTimeType || <span className="to-muted">—</span>}</td>
                    <td>
                      {request.dateRequested ? (
                        new Date(request.dateRequested).toLocaleDateString()
                      ) : (
                        <span className="to-muted">—</span>
                      )}
                    </td>
                    <td>
                      <span className={statusChipClass(request.status)}>
                        {request.status || 'Pending'}
                      </span>
                    </td>
                    <td className="to-col-actions">
                      <div className="button-group">
                        <button
                          className="blue-button"
                          onClick={() => openRequestDetails(request)}
                        >
                          Details
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ----------------------------------- Request form / details ------- */}
      {selectedRequest && (
        <ScopedPortalPopup onClose={closeModal} scopeClass="time-off-page to-popup">
            {selectedRequest.id ? (
              <>
                <h3>Request details</h3>
                <p className="to-modal-lead">
                  {selectedRequest.name || 'Unknown'} · {formatEntryDates(selectedRequest)}
                </p>

                <div className="to-modal-scroll to-detail">
                  <p>
                    <strong>Type:</strong> {describeEntry(selectedRequest)}
                  </p>
                  <p>
                    <strong>Paid time type:</strong> {selectedRequest.paidTimeType || '—'}
                  </p>
                  <p>
                    <strong>Reason:</strong> {selectedRequest.reason || '—'}
                  </p>
                  <p>
                    <strong>Status:</strong>{' '}
                    <span className={statusChipClass(selectedRequest.status)}>
                      {selectedRequest.status || 'Pending'}
                    </span>
                  </p>
                  <p>
                    <strong>Submitted:</strong>{' '}
                    {selectedRequest.dateRequested
                      ? new Date(selectedRequest.dateRequested).toLocaleString()
                      : '—'}
                  </p>

                  <label htmlFor="to-note">Notes</label>
                  <div className="to-notes">
                    {Array.isArray(selectedRequest.notes) &&
                    selectedRequest.notes.length > 0 ? (
                      <ul>
                        {selectedRequest.notes.map((note, index) => (
                          <li key={index}>
                            <span className="to-note-time">{note.timestamp}</span>
                            {note.text}
                            {note.files &&
                              note.files.map((fileURL, fileIndex) => (
                                <a
                                  key={fileIndex}
                                  href={fileURL}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {/\.(jpeg|jpg|gif|png)$/i.test(fileURL) ? (
                                    <img
                                      src={fileURL}
                                      alt={`Attachment ${fileIndex + 1}`}
                                      className="thumbnail"
                                    />
                                  ) : (
                                    ` Attachment ${fileIndex + 1}`
                                  )}
                                </a>
                              ))}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span className="to-sub">No notes yet.</span>
                    )}
                  </div>

                  <div className="to-form-field">
                    <textarea
                      id="to-note"
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      placeholder="Add a note to this request"
                    />
                  </div>
                  <div className="to-form-field">
                    <label htmlFor="to-note-files">Attach files</label>
                    <input
                      id="to-note-files"
                      type="file"
                      multiple
                      onChange={handleFileChange}
                    />
                  </div>
                </div>

                <div className="button-group">
                  <button className="green-button" onClick={saveNote} disabled={uploading}>
                    {uploading ? 'Uploading...' : 'Add note'}
                  </button>
                  <button
                    className="blue-button"
                    onClick={() => setShowDateChangePopup(true)}
                  >
                    Change dates
                  </button>
                  <button className="grey-button" onClick={closeModal}>
                    Close
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3>Request time off</h3>
                <p className="to-modal-lead">
                  Pick the person the time off is for, then the days they need.
                </p>

                <form onSubmit={handleSubmit} style={{ display: 'contents' }}>
                  <div className="to-modal-scroll">
                    <div className="to-form-field">
                      <label htmlFor="to-user">Person</label>
                      <select
                        id="to-user"
                        name="userId"
                        value={timeOffRequest.userId}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select a person</option>
                        {activePeople.map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="to-form-field">
                      <label htmlFor="to-daytype">What do they need?</label>
                      <select
                        id="to-daytype"
                        name="dayType"
                        value={timeOffRequest.dayType}
                        onChange={handleInputChange}
                      >
                        <option value={DAY_FULL}>Full day(s) off</option>
                        <option value={DAY_LEAVE_EARLY}>Leave early</option>
                        <option value={DAY_ARRIVE_LATE}>Come in late</option>
                      </select>
                    </div>

                    {timeOffRequest.dayType === DAY_FULL ? (
                      <div className="to-form-row">
                        <div>
                          <label htmlFor="to-start">Start date</label>
                          <input
                            id="to-start"
                            type="date"
                            name="startDate"
                            value={timeOffRequest.startDate}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div>
                          <label htmlFor="to-end">End date</label>
                          <input
                            id="to-end"
                            type="date"
                            name="endDate"
                            value={timeOffRequest.endDate}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="to-form-row">
                        <div>
                          <label htmlFor="to-start">Date</label>
                          <input
                            id="to-start"
                            type="date"
                            name="startDate"
                            value={timeOffRequest.startDate}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div>
                          <label htmlFor="to-partial-time">
                            {timeOffRequest.dayType === DAY_LEAVE_EARLY
                              ? 'Leaving at'
                              : 'Getting in at'}
                          </label>
                          <input
                            id="to-partial-time"
                            type="time"
                            name="partialTime"
                            value={timeOffRequest.partialTime}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>
                    )}

                    <div className="to-form-field">
                      <label htmlFor="to-type">Paid time type</label>
                      <select
                        id="to-type"
                        name="paidTimeType"
                        value={timeOffRequest.paidTimeType}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select a type</option>
                        <option value="Vacation">Vacation</option>
                        <option value="Personal">Personal</option>
                      </select>
                    </div>

                    <div className="to-form-field">
                      <label htmlFor="to-reason">Reason</label>
                      <textarea
                        id="to-reason"
                        name="reason"
                        value={timeOffRequest.reason}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="button-group">
                    <button type="submit" className="green-button" disabled={submitting}>
                      {submitting ? 'Submitting...' : 'Submit request'}
                    </button>
                    <button type="button" className="grey-button" onClick={closeModal}>
                      Cancel
                    </button>
                  </div>
                </form>
              </>
            )}
        </ScopedPortalPopup>
      )}

      {/* -------------------------------------------- Change dates -------- */}
      {showDateChangePopup && (
        <ScopedPortalPopup
          onClose={() => setShowDateChangePopup(false)}
          scopeClass="time-off-page to-popup"
          level={1}
          width={520}
        >
            <h3>Change dates</h3>
            <p className="to-modal-lead">
              Moving these days runs the same overlap check as a new request.
            </p>
            <div className="to-modal-scroll">
              <div className="to-form-row">
                <div>
                  <label htmlFor="to-new-start">Start date</label>
                  <input
                    id="to-new-start"
                    type="date"
                    value={newStartDate}
                    onChange={(e) => setNewStartDate(e.target.value)}
                    required
                  />
                </div>
                {!isPartialDay(selectedRequest || {}) && (
                  <div>
                    <label htmlFor="to-new-end">End date</label>
                    <input
                      id="to-new-end"
                      type="date"
                      value={newEndDate}
                      onChange={(e) => setNewEndDate(e.target.value)}
                      required
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="button-group">
              <button className="green-button" onClick={handleDateChange}>
                Save dates
              </button>
              <button
                className="grey-button"
                onClick={() => setShowDateChangePopup(false)}
              >
                Cancel
              </button>
            </div>
        </ScopedPortalPopup>
      )}

      {/* ---------------------------------------------------------------
          OVERLAP WARNING + CONFIRMATION

          Sits above the request form and the Change Dates popup (portal
          level 2), so stacking never depends on source order.
      --------------------------------------------------------------- */}
      {conflictPrompt && (
        <ScopedPortalPopup
          onClose={dismissConflictAndEdit}
          scopeClass="time-off-page to-popup"
          level={2}
          role="alertdialog"
        >
            <h3>Someone else already asked for these days</h3>
            <p className="to-modal-lead">
              Requests that stack up on the same days are more likely to be turned
              down. If the plans are flexible, different dates have a much better
              chance of approval.
            </p>

            <div className="to-modal-scroll">
              <p>
                {conflictPeopleLabel(conflictPrompt.conflicts)}{' '}
                {conflictPrompt.conflicts.length === 1 ? 'has' : 'have'} time off
                requested that overlaps this one.
              </p>

              <div className="to-notice">
                <p className="to-notice-title">Overlapping requests</p>
                <ul>
                  {conflictPrompt.conflicts.map((c) => {
                    const days = countDays(c.overlap.start, c.overlap.end);
                    return (
                      <li key={c.id}>
                        <strong>{c.name || 'Unknown user'}</strong>
                        {' - '}
                        {formatRange(c.startDate, c.endDate)}
                        {c.status ? ` (${c.status})` : ''}
                        <div className="to-notice-detail">
                          Shares {days} {days === 1 ? 'day' : 'days'}:{' '}
                          {formatRange(c.overlap.start, c.overlap.end)}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <p className="to-sub">
                You can still go ahead if these are the days you need. This is a
                heads-up, not a block.
              </p>
            </div>

            <div className="button-group">
              <button
                className="grey-button"
                onClick={dismissConflictAndEdit}
                disabled={submitting}
              >
                Pick different dates
              </button>
              <button
                className="amber-button"
                onClick={confirmDespiteConflicts}
                disabled={submitting}
              >
                {submitting
                  ? 'Working...'
                  : conflictPrompt.mode === 'submit'
                  ? 'Submit anyway'
                  : 'Save anyway'}
              </button>
            </div>
        </ScopedPortalPopup>
      )}
    </div>
  );
};

export default TimeOff;
